import { createContext, useContext, useEffect } from "react";

/**
 * What a screen may know about the chrome drawn around it.
 *
 * The screens are shared by two shells (see `HubShell` and `PortalShell`), and
 * two facts about the shell change how a screen draws itself:
 *
 * - `variant`: in the portal the screen's own title is the page's `h1`; in the
 *   hub the greeting above it already is, so the same title is an `h2` there.
 * - `setDocBar`: a document page on a phone pins its one action to the bottom
 *   edge, exactly where the portal's tab bar sits. The page says so and the
 *   shell steps its bar aside, rather than each guessing the other's height.
 *
 * The default is the hub with nothing to step aside, so a screen rendered
 * outside any shell (a test, a harness) behaves like the plainest case.
 */
export interface ShellContextValue {
  variant: "hub" | "portal";
  setDocBar: (on: boolean) => void;
}

export const ShellContext = createContext<ShellContextValue>({
  variant: "hub",
  setDocBar: () => {},
});

export function useShell(): ShellContextValue {
  return useContext(ShellContext);
}

/**
 * Claim the bottom edge of a phone screen for as long as the caller is mounted
 * and `on` holds. The shell's tab bar comes back when either stops being true.
 */
export function useDocBar(on: boolean): void {
  const { setDocBar } = useShell();
  useEffect(() => {
    if (!on) return;
    setDocBar(true);
    return () => setDocBar(false);
  }, [on, setDocBar]);
}
